import { useState, useEffect, useCallback } from 'react';
import type { BillingPlan, PremiumModule } from '@yawi/types';
import { useServices, useToast } from '@/app/providers';

export function useBillingPlans() {
  const { billingRepository } = useServices();
  const { showToast } = useToast();

  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchPlans = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await billingRepository.getAllPlans({ page: 1, limit: 100 });
      setPlans(result.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load plans';
      showToast('error', message);
    } finally {
      setIsLoading(false);
    }
  }, [billingRepository, showToast]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const deletePlan = async (id: string) => {
    try {
      await billingRepository.deletePlan(id);
      showToast('success', 'Plan deleted successfully');
      await fetchPlans();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete plan';
      showToast('error', message);
    }
  };

  return {
    plans,
    isLoading,
    refetch: fetchPlans,
    deletePlan,
  };
}

export function usePremiumModules() {
  const { billingRepository } = useServices();
  const { showToast } = useToast();

  const [modules, setModules] = useState<PremiumModule[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchModules = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await billingRepository.getAllModules({ page: 1, limit: 100 });
      setModules(result.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load modules';
      showToast('error', message);
    } finally {
      setIsLoading(false);
    }
  }, [billingRepository, showToast]);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  const deleteModule = async (id: string) => {
    try {
      await billingRepository.deleteModule(id);
      showToast('success', 'Module deleted successfully');
      await fetchModules();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete module';
      showToast('error', message);
    }
  };

  return {
    modules,
    isLoading,
    refetch: fetchModules,
    deleteModule,
  };
}
